// The smaller chapters (sub-periods) inside the current major period, drawn
// to scale but blurred: the reader sees that they exist and how many are
// left before the next major period, and the full report has what each holds.

// Vimshottari order and years. Sub-periods start with the major period's own
// lord and follow this order, each taking a share of the period by its years.
const ORDER = ["Ketu", "Venus", "Sun", "Moon", "Mars", "Rahu", "Jupiter", "Saturn", "Mercury"];
const YEARS = { Ketu: 7, Venus: 20, Sun: 6, Moon: 10, Mars: 7, Rahu: 18, Jupiter: 16, Saturn: 19, Mercury: 17 };

function toYear(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  return d.getUTCFullYear() + (d.getTime() - start) / (365.25 * 86400000);
}

/**
 * The current major period's sub-periods as shares of the bar, which of them
 * is current, and the next major period. Null when there is no current period.
 */
export function lockedChapters(majorPeriods) {
  const periods = majorPeriods?.periods || [];
  const now = majorPeriods?.now || "";
  const index = periods.findIndex((p) => p.start <= now && now < p.end);
  const period = periods[index];
  if (!period || !(period.lord in YEARS)) return null;

  // Measured back from the end: the period running at birth starts before it.
  const end = toYear(period.end);
  const length = YEARS[period.lord];
  const start = end - length;
  const first = ORDER.indexOf(period.lord);
  let at = start;
  const chapters = ORDER.map((_, i) => {
    const lord = ORDER[(first + i) % ORDER.length];
    const span = (length * YEARS[lord]) / 120;
    const chapter = { from: at, to: at + span, share: span / length };
    at += span;
    return chapter;
  });

  const nowYear = toYear(now);
  const current = chapters.findIndex((c) => c.from <= nowYear && nowYear < c.to);
  if (current < 0) return null;
  return { chapters, current, next: periods[index + 1] || null };
}

export default function LockedChapters({ majorPeriods, onClick }) {
  const data = lockedChapters(majorPeriods);
  if (!data) return null;
  const { chapters, current, next } = data;
  const left = chapters.length - current;

  return (
    <button type="button" onClick={onClick}
      className="group block w-full space-y-2 rounded-xl border border-dashed border-orange-300 bg-orange-50/60 p-3 text-left hover:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500">
      <div className="flex h-6 gap-0.5 overflow-hidden rounded-lg" aria-hidden="true">
        {chapters.map((c, i) => (
          <span key={i} style={{ width: `${c.share * 100}%` }}
            className={i < current ? "bg-gray-200"
              : i === current ? "bg-orange-300"
                : "bg-gradient-to-r from-orange-200 to-amber-200 blur-[2px]"} />
        ))}
      </div>
      <p className="flex items-start gap-2 text-sm text-gray-800">
        <span aria-hidden="true">🔒</span>
        <span>
          <strong className="text-gray-900">
            {left === 1 ? "1 smaller chapter" : `${left} smaller chapters`}
          </strong>{" "}
          {next ? `before your ${next.lord} period` : "left in this period"}. Their dates and what each
          asks of you are in your full report.{" "}
          <span className="font-semibold text-orange-700 group-hover:underline">See what's inside →</span>
        </span>
      </p>
    </button>
  );
}
